import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Camera, Crown, Gift, Layers, Star } from "lucide-react";
import axios from "axios";
import { API, pageVariants } from "../components/app/constants";
import { NoiseOverlay } from "../components/shared";
import { SEO } from "../components/shared/SEO";
import { ShareReferralBox } from "./ShareReferralBox";

export const CreateHub = ({ user, onUserUpdate, onNavigate }) => {
  const [recentPortraits, setRecentPortraits] = useState([]);
  const [loadingPortraits, setLoadingPortraits] = useState(true);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [giftSending, setGiftSending] = useState(false);
  const [giftResult, setGiftResult] = useState(null);

  useEffect(() => {
    if (!user) return;
    const fetchRecent = async () => {
      try {
        const res = await axios.get(`${API}/portraits/${user.id}`);
        setRecentPortraits((res.data.portraits || []).slice(0, 4));
      } catch { /* non-critical */ }
      setLoadingPortraits(false);
    };
    fetchRecent();
  }, [user]);

  if (!user) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Sign in to access your studio</p>
          <button onClick={() => onNavigate("/start")} className="btn-primary" data-testid="hub-signin-btn">Get Started</button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pt-28 pb-20 px-6"
    >
      <NoiseOverlay />
      <SEO title="Your Creative Studio" path="/hub" description="Your AI portrait studio dashboard. Create portraits, replace backgrounds, change outfits. 12 stunning AI styles. Access your gallery and credits." noindex />
      <div className="max-w-5xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-10">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.3em] text-gold mb-2"
          >
            Welcome back
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-3xl md:text-4xl tracking-tight mb-3"
            data-testid="hub-welcome"
          >
            {user.name ? `Hello, ${user.name.split(" ")[0]}` : "Your Studio"}
          </motion.h1>

          {/* Credits Display */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-5 py-2"
            data-testid="hub-credits"
          >
            <Crown className="w-5 h-5 text-gold" />
            <span className="text-lg font-semibold">{user.credits} <span className="text-white/50 text-sm font-normal">Credits</span></span>
            {user.credits < 5 && (
              <button
                onClick={() => onNavigate("/pricing")}
                className="bg-[#D4AF37] text-black text-xs font-bold px-3 py-1 rounded-full hover:bg-[#D4AF37]/80 transition-colors"
                data-testid="hub-buy-credits"
              >
                Buy More
              </button>
            )}
          </motion.div>
        </div>

        {/* Photo Tips Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/[0.03] border border-white/10 rounded-xl px-5 py-4 mb-8"
          data-testid="photo-tips"
        >
          <p className="text-white/70 text-sm leading-relaxed">
            <span className="text-[#D4AF37] font-semibold">Tip for best results:</span> Use a clear, front-facing photo with good lighting and minimal obstructions. If you want specific details, styles, or features in your generated image, make sure to describe them clearly in your text prompt.
          </p>
        </motion.div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-5 mb-12">
          {/* Create Portrait Card */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => onNavigate("/create")}
            className="group text-left p-6 md:p-8 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-gold/30 transition-all relative overflow-hidden"
            data-testid="hub-create-portrait"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-gold/10 transition-colors" />
            <div className="relative z-10">
              <div className="w-12 h-12 bg-gold/10 border border-gold/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                <Camera className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl md:text-2xl mb-2">Create Portrait</h3>
              <p className="text-white/50 text-sm mb-4">Transform your photo into 12 stunning AI styles — LinkedIn, Magazine, Fantasy, Anime & more</p>
              <div className="flex items-center gap-2 text-gold text-sm font-medium">
                <span>Start Creating</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </motion.button>

          {/* Background Replace Card */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => onNavigate("/background")}
            className="group text-left p-6 md:p-8 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-gold/30 transition-all relative overflow-hidden"
            data-testid="hub-replace-background"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-gold/10 transition-colors" />
            <div className="relative z-10">
              <div className="w-12 h-12 bg-gold/10 border border-gold/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                <Layers className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl md:text-2xl mb-2">Replace Background</h3>
              <p className="text-white/50 text-sm mb-4">Remove or swap backgrounds with AI — studio, nature, office, luxury interior & custom scenes</p>
              <div className="flex items-center gap-2 text-gold text-sm font-medium">
                <span>Replace Now</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </motion.button>

          {/* Outfit Change Card */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            onClick={() => onNavigate("/outfit")}
            className="group text-left p-6 md:p-8 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-gold/30 transition-all relative overflow-hidden"
            data-testid="hub-change-outfit"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-gold/10 transition-colors" />
            <div className="relative z-10">
              <div className="w-12 h-12 bg-gold/10 border border-gold/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                <Star className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl md:text-2xl mb-2">Change Outfit</h3>
              <p className="text-white/50 text-sm mb-4">AI-powered clothing change — business suit, streetwear, evening gown, superhero & more</p>
              <div className="flex items-center gap-2 text-gold text-sm font-medium">
                <span>Try Now</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </motion.button>
        </div>

        {/* Recent Creations */}
        {!loadingPortraits && recentPortraits.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-12"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-xl md:text-2xl">Recent Creations</h2>
              <button
                onClick={() => onNavigate("/gallery")}
                className="text-gold text-sm font-medium hover:underline flex items-center gap-1"
                data-testid="hub-view-gallery"
              >
                View All <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {recentPortraits.map((p) => (
                <motion.div
                  key={p.id}
                  className="group relative cursor-pointer rounded-xl overflow-hidden border border-white/10 hover:border-gold/30 transition-all"
                  onClick={() => onNavigate("/gallery")}
                  whileHover={{ y: -4 }}
                >
                  <img
                    src={`data:image/png;base64,${p.image_base64}`}
                    alt={p.style}
                    className="w-full aspect-square object-cover"
                    data-testid={`hub-recent-${p.id}`}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <p className="text-xs text-white/70 capitalize">{(p.style || "").replace(/_/g, " ")}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Gift a Portrait */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-8"
        >
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm uppercase tracking-widest text-[#D4AF37]">Gift a Portrait</span>
            </div>
            <p className="text-sm text-white/60 mb-4">Send a free portrait credit to a friend. They sign up to redeem it!</p>
            
            {giftResult ? (
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                <p className="text-green-300 text-sm mb-2">{giftResult.message}</p>
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={giftResult.gift_url} 
                    readOnly 
                    className="input-luxury text-xs flex-1" 
                    data-testid="gift-url"
                  />
                  <button 
                    onClick={() => { navigator.clipboard.writeText(giftResult.gift_url); }}
                    className="px-3 py-2 bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg text-xs font-medium"
                    data-testid="gift-copy-btn"
                  >
                    Copy
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="email" 
                  value={giftEmail} 
                  onChange={(e) => setGiftEmail(e.target.value)}
                  placeholder="Friend's email"
                  className="input-luxury flex-1 text-sm"
                  data-testid="gift-email-input"
                />
                <button 
                  onClick={async () => {
                    if (!giftEmail) return;
                    setGiftSending(true);
                    try {
                      const res = await axios.post(`${API}/gift/create`, { sender_id: user.id, recipient_email: giftEmail, message: giftMessage });
                      setGiftResult(res.data);
                      onUserUpdate({ ...user, credits: res.data.sender_credits });
                    } catch (err) {
                      alert(err.response?.data?.detail || "Failed to send gift");
                    }
                    setGiftSending(false);
                  }}
                  disabled={giftSending || (user?.credits || 0) < 1}
                  className="px-5 py-2.5 bg-[#D4AF37] hover:bg-[#D4AF37]/80 text-black rounded-xl text-sm font-bold transition-colors disabled:opacity-50"
                  data-testid="gift-send-btn"
                >
                  {giftSending ? "Sending..." : "Send Gift (1 Credit)"}
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Referral Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <ShareReferralBox user={user} onUserUpdate={onUserUpdate} />
        </motion.div>
      </div>
    </motion.div>
  );
};


